import * as React from 'react';
import Button from '@mui/material/Button';
import { styled } from '@mui/material/styles';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import TextField from '@mui/material/TextField';
import Grid from '@mui/material/Grid';
import { Box } from '@mui/system';
import { BarberServices } from '../../types/barber';
import { number, object, string } from 'yup';
import { useFormik } from 'formik';
import { roleContext } from '../../context/RoleContext';
import { CircularProgress, InputAdornment } from '@mui/material';

import {
  addServiceToBarber,
  addServiceToShopBarber,
} from '../../middleware/registration_service/updateRequest';
import { Alert } from '@mui/material';
import { useState, useContext } from 'react';
import { UserRole } from '../../types/role';
import { useProfileData } from '../../context/ProfileDataContext';
import { useAuth0 } from '@auth0/auth0-react';

const BootstrapDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialogContent-root': {
    padding: theme.spacing(2),
  },
  '& .MuiDialogActions-root': {
    padding: theme.spacing(1),
  },
}));

const schema = object().shape({
  name: string().required('Service name is required'),
  price: number().required('Price is required'),
  duration: number().required('Duration is required'),
});

interface AddServicesProps {
  /** Controls the dialog's open state externally (e.g. when embedded inside
   * another dialog such as the Schedule Manager's "Create Booking Slot"
   * modal). When omitted, the component manages its own open state via the
   * internal trigger button (original/default behavior). */
  open?: boolean;
  /** Called when the dialog is dismissed (success or cancel) while in
   * controlled mode. */
  onClose?: () => void;
  /** Hides the component's own "Add Service" trigger button so a caller can
   * render its own trigger and fully control opening via the `open` prop. */
  hideTriggerButton?: boolean;
  /** Overrides the barber id resolved from `useProfileData()`. Needed when
   * this component is rendered outside a `ProfileDataProvider` (e.g. the
   * Schedule Manager route), where the context would otherwise fall back to
   * its uninitialized defaults. */
  barberIdOverride?: bigint;
  /** Overrides the shop id resolved from `useProfileData()`, for the same
   * reason as `barberIdOverride`. */
  shopIdOverride?: bigint;
  /** Called with the submitted service values after it has been created
   * successfully, so the caller can e.g. refetch its own service list and
   * auto-select the newly created service. */
  onCreated?: (service: BarberServices) => void;
}

export default function AddServices({
  open: controlledOpen,
  onClose: onCloseProp,
  hideTriggerButton,
  barberIdOverride,
  shopIdOverride,
  onCreated,
}: AddServicesProps = {}) {
  const [internalOpen, setInternalOpen] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const {
    barberId: ctxBarberId,
    shopId: ctxShopId,
    refreshBarber,
    refreshShopBarber,
  } = useProfileData();
  const role = useContext(roleContext);
  const { getAccessTokenSilently } = useAuth0();

  const isControlled = controlledOpen !== undefined;
  const open = isControlled ? controlledOpen : internalOpen;
  const barberId = barberIdOverride ?? ctxBarberId;
  const shopId = shopIdOverride ?? ctxShopId;

  const initialValues: BarberServices = {
    name: '',
    price: 0,
    duration: 0,
  };

  const formik = useFormik({
    initialValues,
    validationSchema: schema,
    onSubmit: async (values) => {
      setLoading(true);
      setError(null);
      try {
        const token = await getAccessTokenSilently();
        if (role === UserRole.BARBER) {
          await addServiceToBarber(values, barberId, token);
          if (!barberIdOverride) await refreshBarber();
        } else if (role === UserRole.SHOP_BARBER) {
          await addServiceToShopBarber(values, shopId, barberId, token);
          if (!barberIdOverride) await refreshShopBarber();
        }
        onCreated?.(values);
        setSubmitted(true);
        setTimeout(() => {
          setSubmitted(false);
          handleClose();
        }, 2000);
      } catch (err) {
        console.error('Error adding service:', err);
        setError(`Error adding service "${values.name}". Please try again.`);
      } finally {
        setLoading(false);
      }
    },
  });

  const handleClickOpen = () => {
    if (!isControlled) setInternalOpen(true);
  };

  const handleClose = () => {
    if (!isControlled) setInternalOpen(false);
    setError(null);
    setSubmitted(false);
    formik.resetForm();
    onCloseProp?.();
  };

  return (
    <React.Fragment>
      {!hideTriggerButton && (
        <Button
          className="addServicesButton buttonHover"
          sx={{
            p: 1.5,
            bgcolor: '#dd8709',
            mt: 2,
            mb: 1,
            color: 'white',
            mr: 1.5,
            fontFamily: 'GFS Didot',
            fontWeight: 'bold',
            '&:hover': { bgcolor: '#b86e08' },
          }}
          variant="contained"
          onClick={handleClickOpen}
        >
          Add Service
        </Button>
      )}
      <BootstrapDialog
        onClose={handleClose}
        aria-labelledby="customized-dialog-title"
        open={open}
      >
        <form onSubmit={formik.handleSubmit}>
          <DialogTitle
            sx={{
              m: 0,
              p: 2,
              fontFamily: 'GFS Didot',
              fontWeight: 'bold',
              display: 'flex',
              alignItems: 'center',
              gap: 1,
            }}
            id="customized-dialog-title"
          >
            <AddCircleOutlineIcon sx={{ color: '#dd8709' }} />
            Add Service
            <IconButton
              aria-label="close"
              onClick={handleClose}
              sx={{
                position: 'absolute',
                right: 8,
                top: 8,
                color: (theme) => theme.palette.grey[500],
              }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent dividers>
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}
            <Box sx={{ mt: 1 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    value={formik.values.name}
                    name="name"
                    fullWidth
                    id="name"
                    label="Name"
                    onChange={formik.handleChange}
                    error={formik.touched.name && Boolean(formik.errors.name)}
                    helperText={formik.touched.name && formik.errors.name}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    value={formik.values.price}
                    fullWidth
                    id="price"
                    label="Price"
                    name="price"
                    type="number"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <AttachMoneyIcon
                            fontSize="small"
                            sx={{ color: '#dd8709' }}
                          />
                        </InputAdornment>
                      ),
                    }}
                    onChange={formik.handleChange}
                    error={formik.touched.price && Boolean(formik.errors.price)}
                    helperText={formik.touched.price && formik.errors.price}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    value={formik.values.duration}
                    fullWidth
                    id="duration"
                    label="Duration (minutes)"
                    name="duration"
                    type="number"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <AccessTimeIcon
                            fontSize="small"
                            sx={{ color: '#dd8709' }}
                          />
                        </InputAdornment>
                      ),
                    }}
                    onChange={formik.handleChange}
                    error={
                      formik.touched.duration && Boolean(formik.errors.duration)
                    }
                    helperText={
                      formik.touched.duration && formik.errors.duration
                    }
                  />
                </Grid>
              </Grid>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button
              className="buttonHover"
              disabled={loading || submitted}
              type="submit"
              fullWidth
              variant="contained"
              sx={{
                mt: '2em',
                bgcolor: '#dd8709',
                color: '#fff',
                fontFamily: 'GFS Didot',
                fontWeight: 'bold',
                p: 1,
                width: '100%',
                '&:hover': { bgcolor: '#b86e08' },
                '&.Mui-disabled': { bgcolor: '#e0b96a', color: '#fff' },
              }}
            >
              {loading ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                'Add Service'
              )}
            </Button>
          </DialogActions>
        </form>
        {submitted && (
          <Alert
            onClose={() => {
              setSubmitted(false);
            }}
            severity="success"
            sx={{ mt: '0.5em' }}
          >
            Service added successfully
          </Alert>
        )}
      </BootstrapDialog>
    </React.Fragment>
  );
}
